import { ChangeEvent, useEffect, useState } from 'react';

import { Input } from '../../../../components/input';
import { Modal } from '../../../../components/modal';
import { Spinner } from '../../../../components/spinner';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { RefundsData } from '../../../../models/refund';
import { RefundsServices } from '../../../../services/refund';
import { fullDateFormat } from '../../../../utils/dates';
import { errorFormatter, ErrorType } from '../../../../utils/errors';
import { cleanNumber } from '../../../../utils/numbers';
import { validateEmail } from '../../../../utils/validations';
import { PayoutType } from './components/payout-type';
import {
	Button,
	ButtonsContainer,
	HintInput,
	InputContainer,
	ModalBody,
	ModalContainer,
	PayoutTypeSection,
	Section,
	Subtitle,
	Title,
	TitlesContainer,
	TitleSection,
	TransferData,
	TransferDetail,
	TransferDetailContainer,
	TransferItem,
} from './styles';

interface ModalProps {
	show: boolean;
	onClose: () => void;
	selectedRowData?: RefundsData;
	setShowAlert: (value: boolean) => void;
}

export interface Values {
	amount: string;
	clabe: string;
	email: string;
	concept: string;
	reference: string;
	paymentToken: string;
	trackingKey: string;
	requestDate: string;
	nameUser: string;
	status: string;
	loading: boolean;
	buttonDisabled: boolean;
}

export const defaultValues: Values = {
	amount: '',
	clabe: '',
	email: '',
	concept: '',
	reference: '',
	paymentToken: '',
	trackingKey: '',
	nameUser: '',
	requestDate: '',
	status: '',
	loading: false,
	buttonDisabled: true,
};

export type TypesAllowed = 'full' | 'partial';

const RefundsModal = ({ show, onClose, selectedRowData }: ModalProps) => {
	const { merchantSelected } = useMerchant();
	const { handleShowAlert } = useFloatingAlert();
	const [typeSelected, setTypeSelected] = useState<TypesAllowed>('full');
	const [showResumeModal, setShowResumeModal] = useState(false);
	const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
	const [state, setState] = useState<Values>(() =>
		selectedRowData?.amount
			? {
				...defaultValues,
				amount: selectedRowData?.amount.split('$')[1],
				paymentToken: selectedRowData?.paymentId,
				clabe: selectedRowData?.userClabe || "",
				email: selectedRowData?.uncensoredEmail,
				reference: selectedRowData.reference,
				nameUser: selectedRowData.clientname,
			}
			: defaultValues,
	);
	const [cashValidate] = useState<boolean>(
		selectedRowData?.paymentMethod == 'Efectivo' ? true : false,
	);

	const handleCloseModal = () => {
		onClose();
		setShowResumeModal(false);
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		if (name === 'clabe') {
			const regex = /^[0-9]*$/;
			if (!regex.test(value)) {
				return;
			}
			if (value.length > 18) {
				return;
			}
			setState({ ...state, [name]: value });
			return;
		}
		if (name === 'nameUser') {
			const regex = /^[a-zA-Z\s]*$/;
			if (value.length >= 40) return;
			if (regex.test(value)) {
				setState({ ...state, [name]: value.toUpperCase() });
				return;
			}
			return;
		}
		if (name === 'concept') {
			if (value.length >= 40) return;
		}
		if (name === 'amount') {
			const regex = /^[0-9,.]*$/;
			if (!regex.test(value)) {
				return;
			}

			const parts = value.split('.');
			if (parts.length === 2 && parts[1].length > 2) {
				const truncatedDecimal = parts[1].slice(0, 2);
				const newValue = parts[0] + '.' + truncatedDecimal;
				handleSelectType(newValue);

				setState({ ...state, [name]: newValue });
				return;
			}
			handleSelectType(value);
		}

		setState({ ...state, [name]: value });
	};

	const handleSelectType = (amount: string) => {
		if (parseFloat(amount) == (selectedRowData && parseFloat(selectedRowData.amountToSearch) || 0)) {
			setTypeSelected('full');
		} else {
			setTypeSelected('partial');

		}
	};

	const handleContinue = async () => {
		await handleSelectType(state.amount);

		setState((state) => ({ ...state, loading: true }));
		try {
			const response = await RefundsServices.getGenerateRefund({
				merchantCode: merchantSelected?.code as string,
				subType: typeSelected,
				paymentToken: state.paymentToken,
				email: state.email || undefined,
				concept: state.concept,
				amount: +cleanNumber(state.amount),
				beneficiary: state.nameUser,
				clabe: state.clabe
			});

			if (response?.error_refund && response?.error_refund.length > 0) {
				const errorCode = response.error_refund[0].errorCode as ErrorType;
				onClose();

				handleShowAlert({
					message: errorFormatter(errorCode),
					type: 'error',
				});
				return;
			}

			setState((state) => ({
				...state,
				trackingKey: response.resume.trackingKey,
				requestDate: response.resume.requestDate,
				status: response.resume.status,
				loading: false,
			}));
			setShowResumeModal(true);
		} catch (e: any) {
			handleShowAlert({
				message: errorFormatter(e.error_refund[0].errorCode),
				type: 'error',
			});
			onClose();
		}
	};

	const transferResume = [
		{
			item: 'Monto',
			data: `$${state.amount}`,
		},
		{
			item: 'Clave de rastreo',
			data: state.trackingKey,
		},
		{
			item: 'Concepto',
			data: state.concept,
		},
		{
			item: 'CLABE beneficiario',
			data: state.clabe,
		},
		{
			item: 'Nombre beneficiario',
			data: state.nameUser,
		},
		{
			item: 'Correo electrónico',
			data: state.email,
		},
		{
			item: 'Fecha de solicitud',
			data: state.requestDate && fullDateFormat(state.requestDate),
		},
		{
			item: 'Estatus',
			data: 'En proceso',
		},
	];

	const handleValidateName = (value: string) => {
		const valueTrim = value.trim();
		const validRegex = /^([A-Za-záéíóúÁÉÍÓÚñÑ]{2,}\s){1,}[A-Za-záéíóúÁÉÍÓÚñÑ]{2,}$/;

		return validRegex.test(valueTrim);
	};

	const handleVerifyDisabledButton = () => {
		const amountParse = cleanNumber(state.amount);
		const maxAmount = (selectedRowData && parseFloat(selectedRowData.amountToSearch)) || 0;

		if (isNaN(parseFloat(amountParse))) {
			return true;
		}
		if (parseFloat(amountParse) > maxAmount) {
			return true;
		}

		if (state.concept == undefined || state.concept == '') {
			return true;
		}

		if (!handleValidateName(state.nameUser)) {
			return true;
		}

		if (!calcControlDigit(state.clabe)) {
			return true;
		}

		return false;
	};

	useEffect(() => {
		const buttonDisabled = handleVerifyDisabledButton();
		setState({ ...state, buttonDisabled });
	}, [state.amount, state.concept, state.nameUser, state.clabe]);

	const handleFocus = (e: ChangeEvent<HTMLInputElement>) => {
		const { type } = e;
		const { value } = e.target;
		if (type === 'focus') {
			setIsEmailValid(false);
		}

		if (type === 'blur') {
			setIsEmailValid(value.length > 0 ? !validateEmail(value) : false);
		}
	};

	const calcControlDigit = (clabe: string) => {
		if (clabe.length !== 18) return false;

		const clabeWeights = [3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7];
		const clabeList = clabe.split('');
		const clabeInt = clabeList.map((i: string) => Number(i));
		const weighted = [];

		for (let i = 0; i < 18 - 1; i++) {
			weighted.push((clabeInt[i] * clabeWeights[i]) % 10);
		}

		const summed = weighted.reduce((curr, next) => curr + next) % 10;
		const controlDigit = (10 - summed) % 10;

		return controlDigit === clabeInt[17];
	};

	return (
		<Modal title="Devolver pago" show={show} onClose={handleCloseModal} type="medium">
			{state.loading ? (
				<Spinner />
			) : (
				<ModalContainer>
					<ModalBody>
						<TitlesContainer>
							<Title>
								{show && !showResumeModal
									? 'Verifica la información de la devolución'
									: 'En proceso'}
							</Title>

							{show && showResumeModal && (
								<Subtitle>
									La devolución está en proceso de autorización bancaria, consulta el estatus en la
									sección “Consultar devoluciones”
								</Subtitle>
							)}
						</TitlesContainer>
						{show && !showResumeModal ? (
							<>
								{/*<div>
									<TitleSection>Selecciona el tipo</TitleSection>

									<PayoutTypeSection>
										<PayoutType
											type="full"
											title="Total"
											subtitle="Devuelve el monto total del pago"
											isSelected={typeSelected === 'full'}
											onClick={() => {
												handleSelectType('full');
											}}
										/>
										<PayoutType
											type="partial"
											title="Parcial"
											subtitle="Devuelve solo una parte del pago"
											isSelected={typeSelected === 'partial'}
											onClick={() => {
												handleSelectType('partial');
											}}
										/>
									</PayoutTypeSection>
								</div>*/}
								<InputContainer>
									<div>
										<Input
											label="Monto"
											name="amount"
											value={state.amount}
											variant="large"
											onChange={handleChangeInput}
											prefixSymbol="$"
										/>
										<HintInput
											error={
												parseFloat(state.amount) >
												((selectedRowData && parseFloat(selectedRowData.amountToSearch)) || 0)
											}
										>
											Máximo a devolver: {selectedRowData?.amount}
										</HintInput>
									</div>
									<div>
										<Input
											label="CLABE del beneficiario (18 dígitos)"
											name="clabe"
											disabled={!cashValidate}
											value={state.clabe}
											variant="large"
											onChange={handleChangeInput}
										/>
										{!calcControlDigit(state.clabe) && (
											<HintInput error>La CLABE es invalida</HintInput>
										)}
									</div>

									<div>
										<Input
											label="Nombre del beneficiario (máx. 40 caracteres)"
											name="nameUser"
											value={state.nameUser}
											variant="large"
											onChange={handleChangeInput}
											onBlur={() =>
												setState({ ...state, nameUser: state.nameUser.toUpperCase().trim() })
											}
										/>
										{!handleValidateName(state.nameUser) && (
											<HintInput error={!handleValidateName(state.nameUser)}>
												Cada palabra debe contener al menos dos letras
											</HintInput>
										)}
									</div>

									<div>
										<Input
											label="Concepto (máx. 40 caracteres)"
											name="concept"
											value={state.concept}
											variant="large"
											onChange={handleChangeInput}
										/>
									</div>
									<div>
										<Input
											label="Correo electrónico del beneficiario (opcional)"
											name="email"
											value={state.email}
											variant="large"
											onChange={handleChangeInput}
											onFocus={handleFocus}
											onBlur={handleFocus}
										/>
										<HintInput error={isEmailValid}>
											{!isEmailValid ? (
												<>Lo usaremos para notificar el estatus de la devolución</>
											) : (
												<>Debe ser un correo electrónico válido</>
											)}
										</HintInput>
									</div>
								</InputContainer>

								<ButtonsContainer>
									<Button variant="cancel" onClick={onClose}>
										Cancelar
									</Button>
									<Button
										variant="send"
										onClick={handleContinue}
										disabled={state.buttonDisabled}
									>
										Enviar solicitud
									</Button>
								</ButtonsContainer>
							</>
						) : (
							<TransferDetail>
								{transferResume.map(({ item, data }) => (
									<>
										<TransferDetailContainer key={item}>
											<TransferItem>{item}</TransferItem>
											<TransferData>{data}</TransferData>
										</TransferDetailContainer>
									</>
								))}
							</TransferDetail>
						)}
					</ModalBody>
				</ModalContainer>
			)}
		</Modal>
	);
};

export { RefundsModal };
